/**
 * useUserLocationInit
 * 
 * Custom hook to handle initial user location setup on component mount
 * Manages persisted state, geolocation, and deep link detection
 * 
 * @param {Object} params - Hook parameters
 * @param {Object} params.userLocation - Current user location state
 * @param {Function} params.setUserLocation - User location setter
 * @param {Function} params.setSearchLocation - Search location setter
 * @param {Function} params.setSearchLocationName - Search location name setter
 * @param {Function} params.setLocationSource - Location source setter
 * @param {Function} params.setIsGettingLocation - Loading state setter
 * @param {Function} params.getUserLocation - Function to get user's geolocation
 * @param {Function} params.fetchCallback - Callback to fetch data after location is set
 * @param {Object} params.hasInitialized - Ref to track initialization
 * @param {boolean} params.checkDeepLinks - Whether to check for deep link params
 * 
 * @returns {void} - Side effects only
 */

import { useEffect } from 'react'
import { storage } from '../../utils/storage'

export function useUserLocationInit({
  userLocation,
  setUserLocation,
  setSearchLocation,
  setSearchLocationName,
  setLocationSource,
  setIsGettingLocation,
  getUserLocation,
  fetchCallback,
  hasInitialized,
  checkDeepLinks = false
}) {
  useEffect(() => {
    if (hasInitialized.current) return // Only run once on mount
    hasInitialized.current = true

    // If explicit lat/lng deeplink params are present (e.g. from city pages),
    // use those coords for the initial entity fetch but skip GPS/storage init.
    const params = new URLSearchParams(window.location.search)
    const urlLat = parseFloat(params.get('lat'))
    const urlLng = parseFloat(params.get('lng'))
    if (!isNaN(urlLat) && !isNaN(urlLng)) {
      if (fetchCallback) {
        fetchCallback({ latitude: urlLat, longitude: urlLng })
      }
      return
    }

    // Check if there are deep link parameters - if so, skip default location loading
    if (checkDeepLinks) {
      const params = new URLSearchParams(window.location.search)
      const hasDeepLinkParams = params.get('tab') === 'search' && 
        (params.get('city') || params.get('region') || params.get('country'))
      
      if (hasDeepLinkParams) {
        // Deep link will handle location and search
        return
      }
    }

    // Check if there's a saved location from previous session
    const lastMapLocation = storage.get('lastMapLocation', null)
    
    // Fetch fresh data on initial mount
    // Priority: userLocation from state > lastMapLocation from storage > GPS
    if (userLocation || lastMapLocation) {
      // We have a saved location, use it instead of requesting GPS
      const locationToUse = userLocation || {
        latitude: lastMapLocation.latitude,
        longitude: lastMapLocation.longitude
      }
      
      if (!userLocation && lastMapLocation) {
        // State hasn't been set yet, but we have saved location - set it now
        setUserLocation(locationToUse)
        if (setSearchLocationName && lastMapLocation.name) {
          setSearchLocationName(lastMapLocation.name)
        }
      }
      
      // Fetch data using saved location
      if (fetchCallback) {
        fetchCallback(locationToUse)
      }
    } else {
      // No persisted location, get user location automatically on page load
      if (setIsGettingLocation) {
        setIsGettingLocation(true)
      }
      
      getUserLocation({
        onSuccess: (location, locationName) => {
          setUserLocation(location)
          if (setSearchLocation && locationName) {
            setSearchLocation(locationName)
          }
          if (setSearchLocationName && locationName) {
            setSearchLocationName(locationName)
          }
          if (setLocationSource) {
            setLocationSource('geolocation')
          }
          if (fetchCallback) {
            fetchCallback(location)
          }
          if (setIsGettingLocation) {
            setIsGettingLocation(false)
          }
        },
        onError: () => {
          // Error already handled by the hook
          if (setIsGettingLocation) {
            setIsGettingLocation(false)
          }
        },
        hasShownErrorRef: { current: false }
      })
    }
  }, [
    hasInitialized,
    userLocation,
    setUserLocation,
    setSearchLocation,
    setSearchLocationName,
    setLocationSource,
    setIsGettingLocation,
    getUserLocation,
    fetchCallback,
    checkDeepLinks
  ])
}
